import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { RenderCTA, renderCTAES } from '../FormCTA/FormCTA.helpers';

import AppointmentReview, { AppointmentSubmitHandle } from '@src/components/AppointmentReview';
import Confirmation from '@src/components/Confirmation';
import DateAndTime from '@src/components/DateAndTime';
import {
	AdditionalPatientInformationForm,
	FullPatientInformationForm,
	GuarantorInformationForm,
	PatientInformationForm,
} from '@src/components/forms';
import { useAppointmentStore, useFormSectionStore, usePatientInformationFormStore } from '@src/store';
import { slugify } from '@src/utils/slugify';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
import { SuccessCard } from 'types/generated';

interface MobileStepperProps {
	specialFlow?: boolean;
	successCardsCollection: { items: Array<SuccessCard> };
}

export default function MobileStepper({ specialFlow = false, successCardsCollection }: Readonly<MobileStepperProps>) {
	const activeSectionIndex = useFormSectionStore((store) => store.activeSectionIndex);
	const availableFormNodes = useFormSectionStore((store) => store.availableFormNodes);
	const formNodes = useFormSectionStore((store) => store.formNodes);
	const setActiveSection = useFormSectionStore((store) => store.setActiveSection);
	const maxIndexReached = useFormSectionStore((store) => store.maxIndexReached);
	const lockForm = useFormSectionStore((store) => store.lockForm);
	const currentFormNodes = availableFormNodes();
	const firstName = usePatientInformationFormStore((store) => store.firstName);
	const lastName = usePatientInformationFormStore((store) => store.lastName);
	const appointmentSlot = useAppointmentStore((store) => store.appointmentSlot);
	const { t } = useTranslation('stepper');
	const router = useRouter();
	const { locale } = router;

	const handleStepSelction = (index: number) => {
		setActiveSection(index);
	};

	const confirmationString = `${t('confirmationString')}${firstName}`;

	const childRef = useRef<AppointmentSubmitHandle>(null);

	useEffect(() => {
		setTimeout(() => window.scrollTo(0, 0), 0);
	}, [activeSectionIndex]);

	const determineStepSubtext = (stepName: string) => {
		if (stepName === 'Patient Information') {
			return (
				<Typography variant="caption">
					{firstName} {lastName}
				</Typography>
			);
		}

		if (stepName === 'Date & Time' && appointmentSlot?.time) {
			const formattedAppointmentDate = new Intl.DateTimeFormat(locale, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			}).format(new Date(appointmentSlot?.time));

			const appointmentStartTime = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(
				new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? '')
			);

			return (
				<Typography variant="caption">
					{formattedAppointmentDate} - {appointmentStartTime}
				</Typography>
			);
		}

		return null;
	};

	const confirmAppointment = () => {
		childRef?.current?.handleSubmit();
	};

	return (
		<>
			<Box sx={{ padding: 1 }}>
				<Box sx={{ textAlign: 'center' }}>
					<Typography color="success.dark" variant="overline">
						{(!specialFlow && activeSectionIndex === 5) || (specialFlow && activeSectionIndex === 4)
							? t('apptScheduled')
							: t('apptAssistant')}
					</Typography>
				</Box>
				{locale === 'en-US' && (
					<Stepper
						activeStep={activeSectionIndex}
						orientation="vertical"
						sx={{ '.MuiCollapse-root': { transitionDuration: '0s !important' } }}
					>
						{currentFormNodes.map((step, index) => (
							<Step
								key={index}
								index={step.index}
								completed={step.completed || formNodes[activeSectionIndex].name === 'Confirmation'}
							>
								<StepButton
									disabled={lockForm && step.name !== 'Confirmation'}
									onClick={() =>
										maxIndexReached >= step.index ? handleStepSelction(step.index) : null
									}
									optional={determineStepSubtext(step.name)}
									tabIndex={lockForm && step.name !== 'Confirmation' ? 0 : -1}
								>
									<StepLabel
										sx={{
											opacity: lockForm && step.name !== 'Confirmation' ? 0.5 : 1,
											'.MuiSvgIcon-root.Mui-completed': { color: 'secondary.main' },
											'.MuiSvgIcon-root.Mui-active': { color: 'secondary.main' },
										}}
										StepIconProps={{ icon: index + 1 }}
									>
										<Typography
											variant="bodyMediumBook"
											color={
												formNodes[activeSectionIndex].name === step.name
													? 'text.primary'
													: step.completed
													? 'text.interactive'
													: 'text.tertiary'
											}
										>
											{step.name}
										</Typography>
									</StepLabel>
								</StepButton>
								<StepContent
									sx={{
										margin: activeSectionIndex === step.index ? '0.8rem 0 2rem -1rem' : '',
									}}
									TransitionProps={{ unmountOnExit: false }}
								>
									{/* default user flow */}
									{!specialFlow && activeSectionIndex === 0 && (
										<PatientInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 1 && <DateAndTime />}
									{!specialFlow && activeSectionIndex === 2 && (
										<AdditionalPatientInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 3 && (
										<GuarantorInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 4 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>
									)}
									{!specialFlow && activeSectionIndex === 5 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} />
										</>
									)}

									{/* special Motto flow */}
									{specialFlow && activeSectionIndex === 0 && <DateAndTime />}
									{specialFlow && activeSectionIndex === 1 && (
										<FullPatientInformationForm formId={slugify(step.name)} />
									)}
									{specialFlow && activeSectionIndex === 2 && (
										<GuarantorInformationForm formId={slugify(step.name)} />
									)}
									{specialFlow && activeSectionIndex === 3 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>
									)}
									{specialFlow && activeSectionIndex === 4 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} />
										</>
									)}
								</StepContent>
							</Step>
						))}
					</Stepper>
				)}
				{locale === 'es-US' && (
					<Stepper
						activeStep={activeSectionIndex}
						orientation="vertical"
						sx={{ '.MuiCollapse-root': { transitionDuration: '0s !important' } }}
					>
						{currentFormNodes.map((step, index) => (
							<Step
								key={index}
								index={step.index}
								completed={step.completed || formNodes[activeSectionIndex].name === 'Confirmation'}
							>
								<StepButton
									disabled={lockForm && step.name !== 'Confirmation'}
									onClick={() =>
										maxIndexReached >= step.index ? handleStepSelction(step.index) : null
									}
									optional={determineStepSubtext(step.name)}
									tabIndex={lockForm && step.name !== 'Confirmation' ? 0 : -1}
								>
									<StepLabel
										sx={{
											opacity: lockForm && step.name !== 'Confirmation' ? 0.5 : 1,
											'.MuiSvgIcon-root.Mui-completed': { color: 'secondary.main' },
											'.MuiSvgIcon-root.Mui-active': { color: 'secondary.main' },
										}}
										StepIconProps={{ icon: index + 1 }}
									>
										<Typography
											variant="bodyMediumBook"
											color={
												formNodes[activeSectionIndex].name === step.name
													? 'text.primary'
													: step.completed
													? 'text.interactive'
													: 'text.tertiary'
											}
										>
											{step.nameES}
										</Typography>
									</StepLabel>
								</StepButton>
								<StepContent
									sx={{
										margin: activeSectionIndex === step.index ? '0.8rem 0 2rem -1rem' : '',
									}}
									TransitionProps={{ unmountOnExit: false }}
								>
									{/* default user flow */}
									{!specialFlow && activeSectionIndex === 0 && (
										<PatientInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 1 && <DateAndTime />}
									{!specialFlow && activeSectionIndex === 2 && (
										<AdditionalPatientInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 3 && (
										<GuarantorInformationForm formId={slugify(step.name)} />
									)}
									{!specialFlow && activeSectionIndex === 4 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>
									)}
									{!specialFlow && activeSectionIndex === 5 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} />
										</>
									)}

									{/* special Motto flow */}
									{specialFlow && activeSectionIndex === 0 && <DateAndTime />}
									{specialFlow && activeSectionIndex === 1 && (
										<FullPatientInformationForm formId={slugify(step.name)} />
									)}
									{specialFlow && activeSectionIndex === 2 && (
										<GuarantorInformationForm formId={slugify(step.name)} />
									)}
									{specialFlow && activeSectionIndex === 3 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>
									)}
									{specialFlow && activeSectionIndex === 4 && (
										<>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} />
										</>
									)}
								</StepContent>
							</Step>
						))}
					</Stepper>
				)}
			</Box>

			{locale === 'en-US' && RenderCTA(formNodes[activeSectionIndex].name, confirmAppointment)}
			{locale === 'es-US' && renderCTAES(formNodes[activeSectionIndex].name, confirmAppointment)}
		</>
	);
}
